import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import {
  PUBLISH_PREVIEW_ENDPOINTS,
  createPublishPreviewDatasource,
  formatAmount,
} from "../../../utils/publishPreviewServerSide";

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 10;

const ViewDetailsButton = (props) => {
  const { data } = props;
  const { onViewDetails } = props.context || {};

  if (!data?.number) return null;

  return (
    <button
      type="button"
      className="bg-indigo-500 text-white px-2 py-1 rounded text-xs"
      onClick={() => onViewDetails?.(data.number)}
    >
      View Details
    </button>
  );
};

const amountFormatter = (params) => formatAmount(params.value);

/**
 * Pre-publish exposure preview. While only the open digit is known the close
 * digit is undecided, so every jodi starting with the open digit is still live.
 * Once the close digit is known too the jodi is decided and only it is shown.
 */
const PublishPreviewModal = ({
  isOpen,
  onOpenChange,
  onCancel,
  onConfirm,
  message = "Are you sure you want to publish these results?",
  matchId,
  openDigit,
  closeDigit,
  basePath = "/admin",
}) => {
  const gridRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [loadError, setLoadError] = useState("");

  const parsedMatchId = Number(matchId);
  const parsedOpenDigit = Number(openDigit);
  const parsedCloseDigit = Number(closeDigit);

  const isResultDigit = (value) =>
    Number.isInteger(value) && value >= 0 && value <= 9;

  const canPreview =
    Number.isInteger(parsedMatchId) &&
    parsedMatchId > 0 &&
    isResultDigit(parsedOpenDigit);

  const hasCloseDigit =
    closeDigit !== "" && closeDigit != null && isResultDigit(parsedCloseDigit);

  const winningNumber =
    canPreview && hasCloseDigit ? `${parsedOpenDigit}${parsedCloseDigit}` : null;

  const handleViewDetails = useCallback(
    (digit) => {
      const url = `${window.location.origin}${basePath}/jodi-report-users/${parsedMatchId}/${digit}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [basePath, parsedMatchId]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Digit",
        field: "number",
        colId: "number",
        maxWidth: 110,
        cellRenderer: (params) => {
          if (!params.value) return "";
          const isWinner = winningNumber && params.value === winningNumber;
          return (
            <span className={isWinner ? "font-bold text-green-700" : ""}>
              {params.value}
              {isWinner ? " ★" : ""}
            </span>
          );
        },
      },
      {
        headerName: "Total Stake",
        field: "total_stake",
        colId: "total_stake",
        valueFormatter: amountFormatter,
      },
      {
        headerName: "Total Payout",
        field: "total_payout",
        colId: "total_payout",
        valueFormatter: amountFormatter,
      },
      {
        headerName: "Profit / Loss",
        field: "profit_loss",
        colId: "profit_loss",
        valueFormatter: amountFormatter,
        cellClass: (params) =>
          Number(params.value) >= 0 ? "text-green-600" : "text-red-600",
      },
      {
        headerName: "Total Users",
        field: "total_users",
        colId: "total_users",
        maxWidth: 120,
      },
      {
        headerName: "Actions",
        colId: "actions",
        cellRenderer: ViewDetailsButton,
        sortable: false,
        filter: false,
        maxWidth: 140,
      },
    ],
    [winningNumber]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  );

  const datasource = useMemo(
    () =>
      createPublishPreviewDatasource({
        endpoint: PUBLISH_PREVIEW_ENDPOINTS.jodiDigits,
        getBasePayload: () => ({
          matchId: parsedMatchId,
          openDigit: parsedOpenDigit,
          closeDigit: hasCloseDigit ? parsedCloseDigit : null,
        }),
        onMeta: (data) => {
          setLoadError("");
          setMeta(data);
        },
        onError: (error) =>
          setLoadError(error?.message || "Failed to load publish preview"),
      }),
    [parsedMatchId, parsedOpenDigit, parsedCloseDigit, hasCloseDigit]
  );

  const onGridReady = useCallback(
    (params) => {
      gridRef.current = params.api;
      params.api.setGridOption("datasource", datasource);
    },
    [datasource]
  );

  const totals = meta?.totals;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="4xl"
      scrollBehavior="inside"
      classNames={{
        backdrop: "bg-black/35",
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <span className="font-semibold">{message}</span>
              {canPreview && (
                <span className="text-xs font-normal text-gray-500">
                  {meta?.match
                    ? `${meta.match.market_name} — ${meta.match.name}`
                    : "Loading match details..."}
                </span>
              )}
            </ModalHeader>
            <ModalBody>
              {!canPreview ? (
                <p className="text-sm text-gray-600">
                  Jodi exposure preview is not available for this selection.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="rounded bg-gray-100 px-2 py-1">
                      Open digit:{" "}
                      <span className="font-semibold">{parsedOpenDigit}</span>
                    </span>
                    {hasCloseDigit ? (
                      <>
                        <span className="rounded bg-gray-100 px-2 py-1">
                          Close digit:{" "}
                          <span className="font-semibold">
                            {parsedCloseDigit}
                          </span>
                        </span>
                        <span className="rounded bg-green-100 px-2 py-1 text-green-800">
                          Result jodi:{" "}
                          <span className="font-semibold">{winningNumber}</span>
                        </span>
                      </>
                    ) : (
                      <span className="rounded bg-gray-100 px-2 py-1">
                        Possible jodis:{" "}
                        <span className="font-semibold">
                          {parsedOpenDigit}0 – {parsedOpenDigit}9
                        </span>
                      </span>
                    )}
                  </div>

                  {loadError && (
                    <p className="text-sm text-red-600">{loadError}</p>
                  )}

                  <div
                    className="ag-theme-alpine"
                    style={{ height: hasCloseDigit ? 180 : 430, width: "100%" }}
                  >
                    <AgGridReact
                      key={`${parsedMatchId}-${parsedOpenDigit}-${
                        hasCloseDigit ? parsedCloseDigit : "x"
                      }`}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      rowModelType="infinite"
                      cacheBlockSize={PAGE_SIZE}
                      maxBlocksInCache={2}
                      pagination={true}
                      paginationPageSize={PAGE_SIZE}
                      onGridReady={onGridReady}
                      onSortChanged={(params) =>
                        params.api.refreshInfiniteCache()
                      }
                      context={{ onViewDetails: handleViewDetails }}
                      enableCellTextSelection={true}
                      overlayNoRowsTemplate="<span style='padding: 10px; display: block;'>No data found</span>"
                    />
                  </div>

                  {totals && !hasCloseDigit && (
                    <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                      <span>
                        Total stake:{" "}
                        <span className="font-semibold">
                          {formatAmount(totals.total_stake)}
                        </span>
                      </span>
                      <span>
                        Total payout:{" "}
                        <span className="font-semibold">
                          {formatAmount(totals.total_payout)}
                        </span>
                      </span>
                      <span>
                        Net profit / loss:{" "}
                        <span
                          className={
                            Number(totals.profit_loss) >= 0
                              ? "font-semibold text-green-600"
                              : "font-semibold text-red-600"
                          }
                        >
                          {formatAmount(totals.profit_loss)}
                        </span>
                      </span>
                    </div>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                color="danger"
                variant="light"
                onPress={() => {
                  onCancel?.();
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={() => {
                  onConfirm?.();
                  onClose();
                }}
              >
                Yes, Publish
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default PublishPreviewModal;
